import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = signed out, object = signed in
  const [user, setUser] = useState(null);
  const qc = useQueryClient();

  useEffect(() => {
    // Returning from Google: AuthCallback exchanges the session_id first
    if (!window.location.hash.includes("session_id=")) {
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    }
    const onExpired = () => {
      qc.clear();
      setUser(false);
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [qc]);

  const signIn = useCallback(
    (data) => {
      qc.clear();
      setUser(data.user);
      return data.user;
    },
    [qc],
  );

  const googleSignIn = useCallback(
    (sessionId) => api.post("/auth/google/session", { session_id: sessionId }).then((r) => signIn(r.data)),
    [signIn],
  );
  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    qc.clear();
    setUser(false);
  }, [qc]);
  const deleteAccount = useCallback(async () => {
    await api.delete("/auth/account");
    qc.clear();
    setUser(false);
  }, [qc]);

  const value = useMemo(
    () => ({ user, setUser, googleSignIn, logout, deleteAccount }),
    [user, googleSignIn, logout, deleteAccount],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
